import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "util/context/user_context";
import {
  getAlarmSettingApi,
  updateAlarmSettingApi,
  createAlarmSettingApi,
} from "api/user";
import moment from "moment";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  bg: "#F7FAF7",
  amber50: "#FFFBEB",
  amber800: "#92400E",
};

const FONT =
  'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif';

// alarmState 인덱스 0 = 댓글 알림 / 1 = 매칭 알림
function Toggle({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 52,
        height: 30,
        borderRadius: 9999,
        border: "none",
        background: on ? C.greenDark : "#D1D5DB",
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        transition: "background 0.18s ease",
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: on ? 26 : 3,
          width: 24,
          height: 24,
          borderRadius: 9999,
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transition: "left 0.18s ease",
        }}
      />
    </button>
  );
}

function Row({ icon, title, desc, on, onChange, disabled }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 9999,
          background: C.greenBg,
          color: C.greenDark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
        aria-hidden
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: C.sub, lineHeight: 1.5 }}>
          {desc}
        </div>
      </div>
      <Toggle on={on} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function NotificationSettingsScreen({ isMobile }) {
  const { userData } = useUser();
  const navigate = useNavigate();
  const [state, setState] = useState([true, true]); // [댓글, 매칭]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [toast, setToast] = useState("");

  const id = userData?.id;

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    (async () => {
      const data = await getAlarmSettingApi(id);
      if (Array.isArray(data) && data[0]) {
        setDocId(data[0].docId || null);
        try {
          const arr = JSON.parse(data[0].alarmState || "[true,true]");
          if (Array.isArray(arr) && arr.length >= 2) setState([!!arr[0], !!arr[1]]);
        } catch {}
      }
      setLoading(false);
    })();
  }, [id]);

  const save = async (nextState) => {
    if (!id) return;
    setSaving(true);
    const payload = {
      id,
      alarmState: nextState,
      updateDate: moment().format("YYYY-MM-DD HH:mm"),
    };
    let result;
    if (docId) {
      result = await updateAlarmSettingApi({ docId, ...payload });
    } else {
      // alarmSetting 이 아직 없는 회원: 생성
      result = await createAlarmSettingApi({
        ...payload,
        createDate: moment().format("YYYY-MM-DD HH:mm"),
      });
      if (result?.docId) setDocId(result.docId);
    }
    setSaving(false);
    if (result?.success) {
      setToast("저장됨");
      setTimeout(() => setToast(""), 1200);
    } else {
      setToast("저장 실패");
      setTimeout(() => setToast(""), 1600);
    }
  };

  const set = (idx, on) => {
    const next = [...state];
    next[idx] = on;
    setState(next);
    save(next);
  };

  return (
    <div
      style={{
        fontFamily: FONT,
        background: C.bg,
        minHeight: isMobile ? "100%" : "calc(100vh - 72px)",
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto",
          padding: isMobile ? "20px 16px 60px" : "32px 20px 80px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* 헤더 */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              padding: "8px 12px",
              borderRadius: 9999,
              border: `1px solid ${C.line}`,
              background: "#fff",
              color: C.body,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            ← 뒤로
          </button>
        </div>
        <div>
          <div
            style={{
              fontSize: isMobile ? 22 : 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            알림 설정
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            받고 싶은 알림 종류를 켜고 끌 수 있어요.
          </div>
        </div>

        {loading ? (
          <div
            style={{
              padding: 20,
              border: `1px solid ${C.line}`,
              borderRadius: 14,
              background: "#fff",
              color: C.sub,
              fontSize: 13,
              textAlign: "center",
            }}
          >
            불러오는 중...
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Row
              icon="💬"
              title="댓글 알림"
              desc="내가 올린 글에 댓글이 달리면 알림을 받아요."
              on={state[0]}
              onChange={(v) => set(0, v)}
              disabled={saving}
            />
            <Row
              icon="🔧"
              title="매칭 알림"
              desc={
                userData?.userType === "수리기사"
                  ? "내 지역에 새 수리요청이 올라오거나 매칭이 진행되면 알림을 받아요."
                  : "내 수리 요청에 수리기사가 매칭되면 알림을 받아요."
              }
              on={state[1]}
              onChange={(v) => set(1, v)}
              disabled={saving}
            />
          </div>
        )}

        <div
          style={{
            marginTop: 6,
            padding: "12px 14px",
            background: C.amber50,
            border: "1px solid #FDE68A",
            borderRadius: 10,
            color: C.amber800,
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          ※ 알림을 꺼두면 푸시 알림 + 인앱 알림이 모두 발송되지 않습니다.
        </div>

        {toast && (
          <div
            style={{
              alignSelf: "center",
              marginTop: 8,
              padding: "8px 16px",
              background: C.ink,
              color: "#fff",
              borderRadius: 9999,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}
